import React, { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Settings, User, Building, Key, Bell, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { settingsAPI } from '@/lib/api';
import useAuthStore from '@/store/authStore';
import { toast } from 'sonner';

export function SettingsPage() {
  const { user, organization } = useAuthStore();
  const [name, setName] = useState(user?.name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [notifCritical, setNotifCritical] = useState(true);
  const [notifWeekly, setNotifWeekly] = useState(true);
  const [notifNewDevices, setNotifNewDevices] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await settingsAPI.updateProfile({ name });
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar perfil');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      await settingsAPI.updateProfile({ name });
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="settings-page">
      <Header title="Settings" />
      
      <div className="p-6 space-y-6 max-w-4xl">
        {/* Organization */}
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Building className="h-4 w-4" />
              Organização
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da Empresa</Label>
              <Input defaultValue={organization?.name || ''} disabled />
            </div>
            <div className="space-y-2">
              <Label>Plano</Label>
              <div className="p-3 bg-secondary/50 rounded-sm flex items-center justify-between">
                <span className="font-mono uppercase">{organization?.plan || 'Enterprise'}</span>
                <span className="text-xs text-success font-mono bg-success/20 px-2 py-1 rounded">ATIVO</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Profile */}
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <User className="h-4 w-4" />
              Perfil do Usuário
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="settings-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input defaultValue={user?.email || ''} disabled />
            </div>
            <div className="space-y-2">
              <Label>Função</Label>
              <div className="p-3 bg-secondary/50 rounded-sm">
                <span className="font-mono uppercase">{user?.role || 'Admin'}</span>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSaveProfile}
              disabled={isSaving}
              data-testid="save-profile-btn"
            >
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salvar Perfil
            </Button>
          </CardContent>
        </Card>

        {/* Security */}
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Key className="h-4 w-4" />
              Segurança
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Autenticação de Dois Fatores (MFA)</p>
                <p className="text-sm text-muted-foreground">Adicione uma camada extra de segurança à sua conta</p>
              </div>
              <Switch 
                checked={mfaEnabled}
                onCheckedChange={(v) => { setMfaEnabled(v); toast.info(v ? 'MFA será habilitado em breve' : 'MFA desabilitado'); }}
                data-testid="mfa-switch"
              />
            </div>
            <div className="pt-4 border-t border-border space-y-2">
              <p className="text-sm font-medium">Senha</p>
              <div className="flex gap-2">
                <Input type="password" placeholder="Senha atual" className="max-w-[200px]" data-testid="current-password" />
                <Input type="password" placeholder="Nova senha" className="max-w-[200px]" data-testid="new-password" />
                <Button variant="outline" data-testid="change-password-btn">Alterar Senha</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notificações
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Alertas Críticos por Email</p>
                <p className="text-sm text-muted-foreground">Receber notificação imediata de alertas críticos</p>
              </div>
              <Switch 
                checked={notifCritical}
                onCheckedChange={setNotifCritical}
                data-testid="notif-critical-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Relatórios Semanais</p>
                <p className="text-sm text-muted-foreground">Resumo semanal do security score e incidentes</p>
              </div>
              <Switch 
                checked={notifWeekly}
                onCheckedChange={setNotifWeekly}
                data-testid="notif-weekly-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Novos Dispositivos na Rede</p>
                <p className="text-sm text-muted-foreground">Alertar sobre dispositivos não reconhecidos</p>
              </div>
              <Switch 
                checked={notifNewDevices}
                onCheckedChange={setNotifNewDevices}
                data-testid="notif-devices-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* API Keys (informational) */}
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Integrações e API
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { name: 'Suricata IDS/IPS', status: 'Conectado', color: 'text-success' },
                { name: 'SIEM Integration', status: 'Ativo', color: 'text-success' },
                { name: 'Threat Intel Feeds', status: '4 feeds ativos', color: 'text-info' },
                { name: 'Email (SMTP)', status: 'Configurado', color: 'text-success' },
              ].map((integration, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-secondary/50 rounded">
                  <span className="text-sm">{integration.name}</span>
                  <span className={`text-xs font-mono ${integration.color}`}>{integration.status}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Button 
          className="font-display uppercase tracking-wider"
          onClick={handleSaveAll}
          disabled={isSaving}
          data-testid="save-all-settings-btn"
        >
          {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar Todas as Alterações
        </Button>
      </div>
    </div>
  );
}

export default SettingsPage;
